import { useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALES = { en: "en-GB", fr: "fr-FR", nl: "nl-NL", ar: "ar" };

function formatDate(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? String(iso).slice(0, 16).replace("T", " ") : d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}

function Badge({ status }) {
  const tone =
    status === "paid" || status === "approved"
      ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
      : status === "failed" || status === "rejected"
        ? "bg-red-500/10 text-red-300 border-red-500/30"
        : "bg-gold/10 text-gold-soft border-gold/30";
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 font-mono2 text-[10px] uppercase ${tone}`}>
      {status || "new"}
    </span>
  );
}

export default function AccountPage() {
  const { t, lang } = useLanguage();
  const locale = LOCALES[lang] || "en-GB";
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/my/requests`, { withCredentials: true })
      .then((res) => setRequests(res.data))
      .catch(() => setRequests([]));
  }, [user]);

  if (user === undefined) {
    return <main className="pt-[72px] min-h-screen flex items-center justify-center"><p className="font-mono2 text-xs uppercase text-slate-500">{t.admin.loading}</p></main>;
  }
  if (!user) return <Navigate to="/auth" replace />;

  const onLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      navigate("/");
    }
  };

  return (
    <main data-testid="account-page" className="pt-[72px] min-h-screen">
      <div className="mx-auto max-w-5xl px-5 lg:px-10 py-14">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              {user.picture && (
                <img src={user.picture} alt="" data-testid="account-avatar" className="h-10 w-10 rounded-full border border-gold/30 object-cover" />
              )}
              <p className="eyebrow">{t.auth.hello}, {user.name || user.email}</p>
            </div>
            <h1 className="mt-2 font-display text-2xl lg:text-3xl font-bold uppercase">{t.auth.myAccount}</h1>
          </div>
          <button
            type="button"
            data-testid="account-logout-button"
            onClick={onLogout}
            className="btn-outline !py-2.5 !px-5 text-[11px] font-mono2 uppercase self-start"
          >
            <LogOut size={14} /> {t.auth.logout}
          </button>
        </div>

        <h2 className="mt-12 font-display text-lg font-bold uppercase">{t.auth.myRequests}</h2>

        <div className="mt-6">
          {!requests ? null : requests.length === 0 ? (
            <div data-testid="account-requests-empty" className="rounded-xl border border-dashed border-white/15 p-10 text-center">
              <Inbox size={28} className="mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400">{t.auth.noRequests}</p>
              <Link to="/contact" data-testid="account-book-cta" className="btn-gold mt-6 inline-flex text-[11px] font-mono2 uppercase">
                {t.auth.bookCta}
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full min-w-[640px] text-sm" data-testid="account-requests-table">
                <thead>
                  <tr className="bg-white/[0.04]">
                    {t.auth.cols.map((c) => (
                      <th key={c} className="p-4 font-mono2 text-[10px] uppercase text-slate-400">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r, i) => (
                    <tr key={r.id || i} data-testid={`account-request-row-${i}`} className="border-t border-white/10">
                      <td className="p-4 font-mono2 text-xs text-slate-400">{formatDate(r.created_at, locale)}</td>
                      <td className="p-4 font-mono2 text-xs text-gold-soft">{t.contact.serviceOptions[r.service_type] || r.service_type}</td>
                      <td className="p-4 text-xs text-slate-300">{r.pickup_address}</td>
                      <td className="p-4 text-xs text-slate-300">{r.destination_address || "—"}</td>
                      <td className="p-4"><Badge status={r.status} /></td>
                      <td className="p-4"><Badge status={r.payment_status || "unpaid"} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
